import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/ui/SelectField';
import { Spinner } from '../../components/ui/Spinner';
import { BusinessHoursEditor } from '../leadBot';
import { emptyProfileInput } from '../../lib/fitnessBot/profileDefaults';
import { parseInstagramInput, parseYoutubeInput } from '../../lib/fitnessBot/links';
import { MAX_PROFILE_VIDEOS, POLICY_FIELDS, PROFILE_VIDEO_KINDS, TONE_OPTIONS } from '../../types/businessProfile';
import type {
  AddOnRow,
  BusinessProfile,
  BusinessProfileInput,
  FaqRow,
  FeeRow,
  MembershipRow,
  ObjectionRow,
  ProfileVideo,
  TeamMember,
} from '../../types/businessProfile';
import { CheckField, Group, ReadOnlyShell, RowList, TextField, newRowId } from './fields';

// THE BUSINESS PROFILE EDITOR (Run N). One component, TWO HOSTS (the portalAssets pattern):
// it knows no endpoint. The staff page passes a `save` that hits /api/business-profile; the
// client portal will pass its own. ⚠️ It is the ONE place a club's facts are authored: the
// lead bot, and later KyckSocial's AI, read what is saved here and nothing else.

function toInput(p: BusinessProfile | null, timezone: string): BusinessProfileInput {
  if (!p) return emptyProfileInput(timezone);
  const { id: _i, accountId: _a, accountName: _n, createdAt: _c, createdBy: _cb, updatedAt: _u, updatedBy: _ub, updatedBySurface: _s, ...rest } = p;
  return rest;
}

export function ProfileEditor({
  accountName,
  profile,
  timezone,
  save,
  readOnly = false,
}: {
  accountName: string | null;
  profile: BusinessProfile | null;
  timezone: string;
  save: (input: BusinessProfileInput) => Promise<unknown>;
  /** The portal host without setup access: every field is shown and none can be changed (a disabled fieldset), and there is no Save. */
  readOnly?: boolean;
}) {
  const [draft, setDraft] = useState<BusinessProfileInput>(() => toInput(profile, timezone));
  useEffect(() => setDraft(toInput(profile, timezone)), [profile, timezone]);

  const set = <K extends keyof BusinessProfileInput>(key: K, value: BusinessProfileInput[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const club = (patch: Partial<BusinessProfileInput['club']>) => set('club', { ...draft.club, ...patch });
  const hours = (patch: Partial<BusinessProfileInput['hours']>) => set('hours', { ...draft.hours, ...patch });
  const voice = (patch: Partial<BusinessProfileInput['voice']>) => set('voice', { ...draft.voice, ...patch });

  const mutation = useMutation({
    mutationFn: () => save({ ...draft, club: { ...draft.club, instagramUrl: instagram.ok ? instagram.value : draft.club.instagramUrl } }),
    onSuccess: () => toast.success('Business profile saved.'),
    onError: (err: Error) => toast.error(err.message),
  });
  const instagram = parseInstagramInput(draft.club.instagramUrl);
  const videoProblems = draft.videos.map((v) => parseYoutubeInput(v.url));
  const videosValid = videoProblems.every((r) => r.ok);
  const valid = draft.club.displayName.trim() !== '' && instagram.ok && videosValid;
  // An @handle is normalised to the URL as it is saved (and on blur, so the person sees what is stored).
  const normaliseInstagram = () => {
    if (instagram.ok && instagram.value !== draft.club.instagramUrl) club({ instagramUrl: instagram.value });
  };

  const Shell = readOnly ? ReadOnlyShell : 'div';
  return (
    <Shell className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Everything the assistant may say as fact about {accountName ?? 'this club'} comes from this page. If it is not written
        here, the assistant does not know it and hands over instead of guessing.
      </p>

      <Group title="The club">
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField id="club-name" label="Club name (as customers see it)" value={draft.club.displayName} onChange={(v) => club({ displayName: v })} />
          <TextField id="club-phone" label="Club phone (offered only in staffed hours; blank = never offered)" value={draft.club.phone} onChange={(v) => club({ phone: v })} />
          <TextField id="club-address" label="Address" value={draft.club.address} onChange={(v) => club({ address: v })} />
          <TextField id="club-email" label="Email" value={draft.club.email} onChange={(v) => club({ email: v })} />
          <TextField id="club-join" label="Join link (never sent in the first message)" value={draft.club.joinUrl} onChange={(v) => club({ joinUrl: v })} />
          <TextField id="club-review" label="Review link" value={draft.club.reviewUrl} onChange={(v) => club({ reviewUrl: v })} />
        </div>
        <TextField id="club-parking" label="Parking" value={draft.club.parking} onChange={(v) => club({ parking: v })} multiline />
        <div onBlur={normaliseInstagram}>
          <TextField id="club-instagram" label="Instagram (link or @handle). Sent when a lead wants a feel for the club" value={draft.club.instagramUrl} onChange={(v) => club({ instagramUrl: v })} />
        </div>
        {!instagram.ok && <p className="text-sm text-destructive" role="alert">{instagram.message}</p>}
      </Group>

      <Group
        title="Club videos"
        help={`Up to ${MAX_PROFILE_VIDEOS} YouTube links. A Club Overview is sent when a lead wants to see what the club is like; a Personal Introduction when they ask about that person. The description only tells the assistant what is in the video, it is never treated as a fact.`}
      >
        <div className="space-y-2">
          {draft.videos.map((v, i) => {
            const update = (patch: Partial<ProfileVideo>) =>
              set('videos', draft.videos.map((x, j) => (j === i ? { ...x, ...patch } : x)));
            const problem = videoProblems[i];
            return (
              <div key={v.id} className="space-y-2 rounded-md border p-3">
                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <Label htmlFor={`video-kind-${i}`}>Type</Label>
                    <SelectField
                      id={`video-kind-${i}`}
                      value={v.kind}
                      onValueChange={(k) => update({ kind: k as ProfileVideo['kind'], ...(k === 'club_overview' ? { teamMemberId: null } : {}) })}
                      options={PROFILE_VIDEO_KINDS.map((k) => ({ value: k.value, label: k.label }))}
                    />
                  </div>
                  <TextField id={`video-url-${i}`} label="YouTube link" value={v.url} onChange={(url) => update({ url })} />
                  <div className="sm:col-span-2">
                    <TextField id={`video-desc-${i}`} label="What's in the video" value={v.description} onChange={(description) => update({ description })} />
                  </div>
                  {v.kind === 'personal_introduction' && (
                    <div className="sm:col-span-2">
                      <Label htmlFor={`video-who-${i}`}>Who's in it (optional)</Label>
                      <SelectField
                        id={`video-who-${i}`}
                        value={v.teamMemberId && draft.team.some((m) => m.id === v.teamMemberId) ? v.teamMemberId : ''}
                        onValueChange={(teamMemberId) => update({ teamMemberId: teamMemberId === '' ? null : teamMemberId })}
                        options={[
                          { value: '', label: 'Nobody in particular' },
                          ...draft.team.filter((m) => m.name.trim() !== '').map((m) => ({ value: m.id, label: m.role.trim() ? `${m.name} (${m.role})` : m.name })),
                        ]}
                      />
                    </div>
                  )}
                </div>
                {problem && !problem.ok && v.url.trim() !== '' && <p className="text-sm text-destructive" role="alert">{problem.message}</p>}
                {problem && !problem.ok && v.url.trim() === '' && <p className="text-sm text-muted-foreground">Add the YouTube link, or remove this video.</p>}
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" onClick={() => set('videos', draft.videos.filter((_x, j) => j !== i))}>Remove</Button>
                </div>
              </div>
            );
          })}
          <Button
            variant="outline"
            size="sm"
            disabled={draft.videos.length >= MAX_PROFILE_VIDEOS}
            onClick={() => set('videos', [...draft.videos, { id: newRowId(), kind: 'club_overview', url: '', description: '', teamMemberId: null }])}
          >
            Add a video
          </Button>
        </div>
      </Group>

      <Group
        title="Hours"
        help="Staffed hours are the one source for: when the team can take a handover, the wording of a handover out of hours, staff alerts, and (when turned on) the tour times offered."
      >
        <CheckField label="24/7 member access" checked={draft.hours.access24x7} onChange={(v) => hours({ access24x7: v })} />
        <TextField id="hours-access" label="Access hours, in words" value={draft.hours.accessHoursText} onChange={(v) => hours({ accessHoursText: v })} />
        <BusinessHoursEditor value={draft.hours.staffedHours} onChange={(v) => hours({ staffedHours: v })} />
        <TextField id="hours-holiday" label="Public holiday note" value={draft.hours.publicHolidayNote} onChange={(v) => hours({ publicHolidayNote: v })} />
        <TextField id="hours-tour" label="Tour times guidance" value={draft.hours.tourTimesGuidance} onChange={(v) => hours({ tourTimesGuidance: v })} multiline />
      </Group>

      <Group title="Team" help="Team members with 'receives alerts' on, and a mobile, are texted for handovers, bookings and distress. Distress goes to all of them.">
        <RowList<TeamMember>
          title="People"
          rows={draft.team}
          columns={[
            { key: 'name', label: 'Name' },
            { key: 'role', label: 'Role' },
            { key: 'mobile', label: 'Mobile' },
            { key: 'email', label: 'Email' },
            { key: 'receivesAlerts', label: 'Receives alerts', kind: 'check' },
          ]}
          onChange={(rows) => set('team', rows)}
          blank={() => ({ id: newRowId(), name: '', role: '', mobile: '', email: '', receivesAlerts: true })}
          addLabel="Add a person"
        />
      </Group>

      <Group title="What makes the club different">
        <TextField id="diff-top" label="Top reasons people join" value={draft.differentiators.topReasons} onChange={(v) => set('differentiators', { ...draft.differentiators, topReasons: v })} multiline />
        <TextField id="diff-typical" label="Typical member" value={draft.differentiators.typicalMember} onChange={(v) => set('differentiators', { ...draft.differentiators, typicalMember: v })} multiline />
        <TextField id="diff-comp" label="Local competitors (never run down by name)" value={draft.differentiators.competitors} onChange={(v) => set('differentiators', { ...draft.differentiators, competitors: v })} multiline />
        <TextField id="diff-fac" label="Facilities" value={draft.differentiators.facilities} onChange={(v) => set('differentiators', { ...draft.differentiators, facilities: v })} multiline />
        <TextField id="diff-not" label="Things the club does NOT have" value={draft.differentiators.doNotHave} onChange={(v) => set('differentiators', { ...draft.differentiators, doNotHave: v })} multiline />
      </Group>

      <Group title="Memberships and prices" help="The ONLY prices the assistant may say are the ones written here (and in live promotions). Any other figure in a reply is held and the team is told.">
        <RowList<MembershipRow>
          title="Memberships"
          rows={draft.memberships}
          columns={[
            { key: 'name', label: 'Name' },
            { key: 'price', label: 'Price (e.g. 28.95)' },
            { key: 'frequency', label: 'Frequency' },
            { key: 'minimumTerm', label: 'Minimum term' },
            { key: 'included', label: 'What is included', kind: 'textarea' },
          ]}
          onChange={(rows) => set('memberships', rows)}
          blank={() => ({ id: newRowId(), name: '', price: '', frequency: 'Weekly', minimumTerm: '', included: '' })}
          addLabel="Add a membership"
        />
        <RowList<FeeRow>
          title="Fees"
          rows={draft.fees}
          columns={[
            { key: 'label', label: 'Fee' },
            { key: 'value', label: 'Amount or note' },
          ]}
          onChange={(rows) => set('fees', rows)}
          blank={() => ({ id: newRowId(), label: '', value: '' })}
          addLabel="Add a fee"
        />
        <RowList<AddOnRow>
          title="Add-ons"
          rows={draft.addOns}
          columns={[
            { key: 'service', label: 'Service' },
            { key: 'price', label: 'Price' },
            { key: 'whenToRaise', label: 'When the assistant may raise it', kind: 'textarea' },
          ]}
          onChange={(rows) => set('addOns', rows)}
          blank={() => ({ id: newRowId(), service: '', price: '', whenToRaise: '' })}
          addLabel="Add an add-on"
        />
      </Group>

      <Group title="Policies">
        {POLICY_FIELDS.map((f) => (
          <TextField
            key={f.key}
            id={`policy-${f.key}`}
            label={f.label}
            value={draft.policies[f.key]}
            onChange={(v) => set('policies', { ...draft.policies, [f.key]: v })}
            multiline
          />
        ))}
      </Group>

      <Group title="Voice">
        <div className="space-y-1">
          <Label>Tone</Label>
          <div className="flex flex-wrap gap-3">
            {TONE_OPTIONS.map((t) => (
              <CheckField
                key={t}
                label={t}
                checked={draft.voice.tones.includes(t)}
                onChange={(on) => voice({ tones: on ? [...draft.voice.tones, t] : draft.voice.tones.filter((x) => x !== t) })}
              />
            ))}
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="voice-emoji">Emoji</Label>
            <SelectField
              id="voice-emoji"
              value={draft.voice.emoji}
              onValueChange={(v) => voice({ emoji: v as typeof draft.voice.emoji })}
              options={[
                { value: 'no', label: 'None' },
                { value: 'sparingly', label: 'Sparingly' },
                { value: 'yes', label: 'Yes' },
              ]}
            />
          </div>
          <div>
            <Label htmlFor="voice-reo">Te reo</Label>
            <SelectField
              id="voice-reo"
              value={draft.voice.teReo}
              onValueChange={(v) => voice({ teReo: v as typeof draft.voice.teReo })}
              options={[
                { value: 'no', label: 'No' },
                { value: 'occasionally', label: 'Occasionally' },
                { value: 'yes', label: 'Yes' },
              ]}
            />
          </div>
        </div>
        <CheckField label="Use first names" checked={draft.voice.useFirstNames} onChange={(v) => voice({ useFirstNames: v })} />
        <TextField id="voice-slang" label="Local slang" value={draft.voice.localSlang} onChange={(v) => voice({ localSlang: v })} />
        <TextField id="voice-never" label="Never say" value={draft.voice.neverSay} onChange={(v) => voice({ neverSay: v })} multiline />
        <TextField id="voice-always" label="Always say" value={draft.voice.alwaysSay} onChange={(v) => voice({ alwaysSay: v })} multiline />
        <TextField id="voice-sample" label="A message that sounds like you" value={draft.voice.sampleMessage} onChange={(v) => voice({ sampleMessage: v })} multiline />
      </Group>

      <Group title="Questions people ask" help="Tick 'hand over' on a question the assistant must NOT answer itself (for example a student discount).">
        <RowList<FaqRow>
          title="FAQs"
          rows={draft.faqs}
          columns={[
            { key: 'question', label: 'Question', wide: true },
            { key: 'answer', label: 'Answer', kind: 'textarea' },
            { key: 'handover', label: 'Hand over to the team instead of answering', kind: 'check' },
          ]}
          onChange={(rows) => set('faqs', rows)}
          blank={() => ({ id: newRowId(), question: '', answer: '', handover: false })}
          addLabel="Add a question"
        />
        <RowList<ObjectionRow>
          title="Objections"
          rows={draft.objections}
          columns={[
            { key: 'theySay', label: 'They say', wide: true },
            { key: 'weReply', label: 'We reply', kind: 'textarea' },
          ]}
          onChange={(rows) => set('objections', rows)}
          blank={() => ({ id: newRowId(), theySay: '', weReply: '' })}
          addLabel="Add an objection"
        />
      </Group>

      <Group title="Discounting and hard stops">
        <CheckField label="The assistant may discount" checked={draft.discounting.canDiscount} onChange={(v) => set('discounting', { ...draft.discounting, canDiscount: v })} />
        <TextField id="disc-push" label="What it says when someone pushes for a discount" value={draft.discounting.pushBackReply} onChange={(v) => set('discounting', { ...draft.discounting, pushBackReply: v })} multiline />
        <TextField id="hard-stops" label="Extra hard stops (topics always handed to a person)" value={draft.extraHardStops} onChange={(v) => set('extraHardStops', v)} multiline />
      </Group>

      <Group title="Fixed messages" help="Blank uses the shipped wording. The STOP footer is added by the server, not by these.">
        <TextField id="msg-handover" label="Handover message" value={draft.messages.handover} onChange={(v) => set('messages', { ...draft.messages, handover: v })} multiline />
        <TextField id="msg-notsure" label="Not-sure message" value={draft.messages.notSure} onChange={(v) => set('messages', { ...draft.messages, notSure: v })} multiline />
        <TextField id="msg-optout" label="Opt-out confirmation" value={draft.messages.optOutConfirmation} onChange={(v) => set('messages', { ...draft.messages, optOutConfirmation: v })} multiline />
        <TextField id="msg-distress" label="Distress message (check the crisis line is right for your country)" value={draft.messages.distress} onChange={(v) => set('messages', { ...draft.messages, distress: v })} multiline />
        <CheckField label="Add the Reply STOP line to the first message" checked={draft.messages.includeStopFooter} onChange={(v) => set('messages', { ...draft.messages, includeStopFooter: v })} />
      </Group>

      <Group title="Tours">
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label htmlFor="tour-minutes">Tour length (minutes)</Label>
            <Input
              id="tour-minutes"
              inputMode="numeric"
              value={String(draft.booking.tourMinutes)}
              onChange={(e) => set('booking', { ...draft.booking, tourMinutes: Number(e.target.value.replace(/\D/g, '')) || 0 })}
            />
          </div>
        </div>
        <TextField id="tour-bring" label="What to bring" value={draft.booking.whatToBring} onChange={(v) => set('booking', { ...draft.booking, whatToBring: v })} multiline />
        <TextField id="tour-where" label="Where to go when they arrive" value={draft.booking.whereToGo} onChange={(v) => set('booking', { ...draft.booking, whereToGo: v })} multiline />
      </Group>

      {readOnly ? null : (
        <div className="flex justify-end">
          <Button onClick={() => mutation.mutate()} disabled={!valid || mutation.isPending}>
            {mutation.isPending ? <Spinner className="h-4 w-4" /> : null}
            Save business profile
          </Button>
        </div>
      )}
    </Shell>
  );
}
